/**
 * Filtrování a řazení placementů v multiselectu (02 FR-1).
 *
 * Hledá se v `PlacementOption.search` (lowercase blob z backendu: web, zařízení,
 * dodavatel, fáze, pozice, group, strana `r2b2` / `px konkurence`) a navíc v
 * `placement_id` a čitelném labelu — vložené `mobile_vlasta.cz_animTestA_sticky`
 * i text, který uživatel vidí v seznamu, musí najít to, co čeká.
 *
 * Pravidlo shody per slovo dotazu (bez ohledu na velikost písmen a diakritiku):
 *   - jedno písmeno → nějaké slovo začíná tím písmenem (`v` → vlasta). Substring by
 *     u jednoho písmene chytil skoro všechno, celé slovo zase vyprázdnilo seznam
 *     po prvním stisku klávesy.
 *   - delší dotaz   → substring kdekoli (`lifee` najde i `bme.lifee.cz`,
 *     `animtesta_sticky` najde placement_id).
 *
 * Víc slov = AND (`vlasta px` = PX pozice na vlasta.cz).
 */

/** Lowercase bez diakritiky. */
export function normalizeSearch(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** Odpovídá text dotazu? Prázdný dotaz matchne vše. */
export function matchesPlacementSearch(haystack: string, query: string): boolean {
  // slova bez písmen/číslic (oddělovač „·" zkopírovaný z labelu) nic nefiltrují
  const words = normalizeSearch(query)
    .split(/\s+/)
    .filter((w) => /[a-z0-9]/.test(w));
  if (words.length === 0) return true;
  const text = normalizeSearch(haystack);
  const tokens = text.split(/[^a-z0-9]+/).filter(Boolean);
  return words.every((word) =>
    word.length === 1 ? tokens.some((t) => t.startsWith(word)) : text.includes(word),
  );
}

export interface HasSearch {
  search: string;
  placement_id?: string;
  label_cs?: string;
}

const haystackOf = (item: HasSearch) =>
  [item.search, item.placement_id ?? '', item.label_cs ?? ''].join(' ');

/** Vyfiltruje seznam placementů podle dotazu; vstupní pořadí zachová. */
export function filterPlacements<T extends HasSearch>(items: readonly T[], query: string): T[] {
  const trimmed = query.trim();
  if (!trimmed) return [...items];
  return items.filter((item) => matchesPlacementSearch(haystackOf(item), trimmed));
}

const collator = new Intl.Collator('cs', { numeric: true, sensitivity: 'base' });

interface Sortable {
  label_cs: string;
  placement_id: string;
  domain?: string;
  position?: string;
  type?: string;
}

/** Abecedně: web, pozice, zařízení, pak placement_id (v něm je group). Po polích, ne
 *  podle labelu — collator v labelu přeskakuje interpunkci a `animTestA_refresh`
 *  by předběhl `animTestA`. */
export function sortPlacements<T extends Sortable>(items: readonly T[]): T[] {
  const keys = (x: T): string[] => [
    x.domain || x.label_cs,
    x.position ?? '',
    x.type ?? '',
    x.placement_id,
  ];
  return [...items].sort((a, b) => {
    const ka = keys(a);
    const kb = keys(b);
    for (let i = 0; i < ka.length; i += 1) {
      const c = collator.compare(ka[i] ?? '', kb[i] ?? '');
      if (c) return c;
    }
    return 0;
  });
}
